<template>
    <v-dialog
        v-model="dialogModel"
        width="768"
        max-width="95%"
        @click:outside.prevent="closeDialog"
    >
        <v-card>
            <v-card-title class="text-h6 text-md-h5 grey lighten-2">
                <v-row>
                    <v-col cols="10">
                        Staff Uniforms
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="py-5" style="max-height: 75vh; overflow-y: auto;">
                <v-form ref="staffUniformForm" lazy-validation>
                    <p class="body-2 mb-4">
                        At Synergy Dental Clinic Ltd, all staff are given a staff uniform. All staff must ensure that the uniforms are worn daily and are kept clean at all times.
                        Staff uniforms are the property of Synergy Dental Clinic. No uniform is to be worn outside of the practice.
                    </p>
                    <p class="body-2 mb-4">
                        <b>Disclaimer:</b> Synergy Dental Clinic Ltd reserves the right to deduct your pay if new uniforms are required to be ordered due to negligence.
                        I understand that I am responsible for keeping my uniform clean and in good condition. I also understand that all uniform is the property of
                        Synergy Dental Clinic, and is to be returned should my contract be terminated or following resignation.
                    </p>

                    <v-row class="align-end mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Full Name <span class="red--text">*</span></h3>
                            <v-text-field
                                v-model="form.fullName.firstName"
                                placeholder="First Name"
                                outlined
                                dense
                                hint="First Name"
                                :persistent-hint="true"
                                :rules="firstNameRules"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="form.fullName.lastName"
                                placeholder="Last Name"
                                outlined
                                dense
                                hint="Last Name"
                                :persistent-hint="true"
                                :rules="lastNameRules"
                            />
                        </v-col>
                    </v-row>

                    <v-row class="mb-4">
                        <v-col cols="12" md="6">
                            <h3 class="mb-4">Clothing Size <span class="red--text">*</span></h3>
                            <v-select
                                v-model="form.clothingSize"
                                :items="clothingSizes"
                                placeholder="Please Select"
                                outlined
                                dense
                                :rules="[requiredRules]"
                                attach
                            />
                        </v-col>
                    </v-row>

                    <v-row dense class="mb-4">
                        <v-col cols="12">
                            <h3 class="mb-2">Date <span class="red--text">*</span></h3>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu
                                v-model="dateMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.dateFormatted"
                                        placeholder="Date"
                                        readonly
                                        outlined
                                        dense
                                        prepend-icon="mdi-calendar"
                                        v-bind="attrs"
                                        v-on="on"
                                        :rules="[requiredRules]"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="form.date"
                                    @input="dateMenu = false"
                                />
                            </v-menu>
                        </v-col>
                    </v-row>

                    <div class="mt-5 pt-5" style="border-top:1px solid #e0e0e0">
                        <div class="d-flex justify-end">
                            <v-btn color="primary" :loading="submitLoading" @click="onSubmit">
                                Submit
                            </v-btn>
                        </div>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import validationRules from '@/common/validationRules'
    import { mapActions } from 'vuex'

    const emptyForm = () => ({
        fullName: {
            firstName: '',
            lastName: ''
        },
        clothingSize: '',
        date: null,
        dateFormatted: ''
    })

    export default {
        name: 'StaffUniformFrom',
        props: ['isOpen', 'programme'],
        data: () => ({
            form: emptyForm(),
            submitLoading: false,
            dateMenu: false,
            firstNameRules: validationRules.firstName,
            lastNameRules: validationRules.lastName,
            requiredRules: validationRules.required,
            clothingSizes: [
                'Extra Small',
                'Small',
                'Medium',
                'Large',
                'Extra Large',
                'Double Extra Large'
            ]
        }),
        computed: {
            dialogModel: {
                get () {
                    return this.isOpen
                },
                set (value) {
                    this.$emit('update:isOpen', value)
                }
            }
        },
        watch: {
            'form.date' (val) {
                this.form.dateFormatted = val ? Date.formatDate([val]).trim() : ''
            }
        },
        methods: {
            ...mapActions([
                'uploadStaffUniformLogForm', 'showAlert'
            ]),
            buildPayload () {
                return {
                    transitionProgrammeCourseId: this.programme && this.programme.id ? this.programme.id : 0,
                    firstName: this.form.fullName.firstName,
                    lastName: this.form.fullName.lastName,
                    clothingSize: this.form.clothingSize,
                    date: this.form.date,
                    formType: 'StaffUniformLog'
                }
            },
            resetForm () {
                this.form = emptyForm()
            },
            closeDialog () {
                this.resetForm()
                this.$emit('update:isOpen', false)
            },
            async onSubmit () {
                const formRef = this.$refs.staffUniformForm
                if (!formRef.validate()) {
                    this.showAlert({
                        text: 'Fill all the required fields please',
                        type: 'error'
                    })
                    return
                }

                this.submitLoading = true
                try {
                    const payload = this.buildPayload()
                    await this.uploadStaffUniformLogForm(payload)
                    this.$emit('saved')
                    this.closeDialog()
                    if (formRef && formRef.resetValidation) formRef.resetValidation()
                } catch (e) {
                    this.showAlert({
                        text: e.message || 'Error saving form',
                        type: 'error'
                    })
                } finally {
                    this.submitLoading = false
                }
            }
        }
    }
</script>
